/**
 * useSites — multi-site annual plans. Fetches the signed-in user's
 * sites, tracks the active selection (persisted per browser) and
 * exposes CRUD helpers. Guests get an empty list and stay on the
 * single "default" plan.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import { API, logDev, safeStorage } from "../lib/beaconUtils";

/**
 * Merge the applied company profile with the active site so the
 * dynamic-plan engine tailors each site's calendar. Site notes are
 * appended to free_text_context; the default site with no extra
 * context leaves the profile untouched (same context hash → the
 * existing cached plan keeps working).
 */
export function mergeSiteProfile(appliedProfile, site) {
  if (!site || !appliedProfile) return appliedProfile;
  const merged = { ...appliedProfile, site_name: site.name };
  const extra = (site.site_context || "").trim();
  const siteNote = site.is_default
    ? extra
    : `Site: ${site.name}.${extra ? ` ${extra}` : ""}`;
  if (!siteNote) return merged;
  const base = (appliedProfile.free_text_context || "").trim();
  const sep = base ? (base.endsWith(".") ? " " : ". ") : "";
  merged.free_text_context = `${base}${sep}${siteNote}`.slice(0, 500);
  return merged;
}

export function useSites(authUser) {
  const [sites, setSites] = useState([]);
  const [tier, setTier] = useState("basic");
  const [maxSites, setMaxSites] = useState(1);
  const [activeSiteId, setActiveSiteId] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("beacon-active-site");
  });
  const signedIn = Boolean(authUser?.user_id);

  const reloadSites = useCallback(async () => {
    if (!signedIn) {
      setSites([]);
      return;
    }
    try {
      const r = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(r.data.sites);
      setTier(r.data.tier);
      setMaxSites(r.data.max_sites);
    } catch (e) {
      logDev("Sites load failed", e);
    }
  }, [signedIn]);

  useEffect(() => { reloadSites(); }, [reloadSites]);

  const activeSite = useMemo(() => {
    if (!sites.length) return null;
    return (
      sites.find((s) => s.site_id === activeSiteId) ||
      sites.find((s) => s.is_default) ||
      sites[0]
    );
  }, [sites, activeSiteId]);

  const selectSite = useCallback((siteId) => {
    setActiveSiteId(siteId);
    safeStorage.set("beacon-active-site", siteId);
  }, []);

  const addSite = useCallback(async (name, siteContext) => {
    const r = await axios.post(
      `${API}/sites`,
      { name, site_context: siteContext || null },
      { withCredentials: true },
    );
    await reloadSites();
    return r.data.site;
  }, [reloadSites]);

  const updateSite = useCallback(async (siteId, body) => {
    await axios.patch(`${API}/sites/${siteId}`, body, {
      withCredentials: true,
    });
    await reloadSites();
  }, [reloadSites]);

  const deleteSite = useCallback(async (siteId) => {
    await axios.delete(`${API}/sites/${siteId}`, { withCredentials: true });
    if (siteId === activeSiteId) {
      safeStorage.remove("beacon-active-site");
      setActiveSiteId(null);
    }
    await reloadSites();
  }, [activeSiteId, reloadSites]);

  return {
    sites,
    tier,
    maxSites,
    activeSite,
    activeSiteId: activeSite?.site_id || null,
    planId: activeSite?.plan_id || "default",
    selectSite,
    addSite,
    updateSite,
    deleteSite,
    reloadSites,
  };
}
